import type {
  BirthdayMemberEntity,
  DayDataEntity,
  SlotParticipantEntity,
  WeeklyTrialsDropInsEntity,
} from "@/modules/boxDashboard/modules/TrialsDropIns/entities/weeklyTrialsDropInsEntity"
import { Language } from "@/modules/language/utils/languageUtils"
import dayjs from "dayjs"

export type SlotParticipantModel = {
  boxMemberId: string
  athleteName: string
  coachName: string | null
  time: string | null
  programName: string | null
}

export type BirthdayMemberModel = {
  boxMemberId: string
  athleteName: string
  coachName: string | null
  time: string | null
  programName: string | null
  nextSessionDate: string | null
  lastSessionDateInDays: number | null
}

export type DayIndicatorModel = {
  dayOfWeek: string
  dayOfMonth: number
  isToday: boolean
  numberOfTrials: number
  numberOfDropIns: number
  numberOfBirthdays: number
  hasTrials: boolean
  hasDropIns: boolean
  hasBirthdays: boolean
  trials: SlotParticipantModel[]
  dropIns: SlotParticipantModel[]
  birthdaysPresent: BirthdayMemberModel[]
  birthdaysAbsent: BirthdayMemberModel[]
}

export type WeeklyTrialsDropInsModel = {
  weekLabel: string
  totalTrials: number
  totalDropIns: number
  totalBirthdays: number
  days: DayIndicatorModel[]
  canNavigatePrevious: boolean
  canNavigateNext: boolean
}

function createSlotParticipantModel(slotParticipant: SlotParticipantEntity): SlotParticipantModel {
  return {
    boxMemberId: slotParticipant.boxMemberId,
    athleteName: slotParticipant.getAthleteFullName(),
    coachName: slotParticipant.getCoachFullName(),
    time: slotParticipant.getTime(),
    programName: slotParticipant.programName,
  }
}

function createBirthdayMemberModel(birthdayMember: BirthdayMemberEntity): BirthdayMemberModel {
  return {
    boxMemberId: birthdayMember.boxMemberId,
    athleteName: birthdayMember.getAthleteFullName(),
    coachName: birthdayMember.getCoachFullName(),
    time: birthdayMember.getTime(),
    programName: birthdayMember.programName,
    nextSessionDate: birthdayMember.getNextSessionDateFormated(),
    lastSessionDateInDays: birthdayMember.getLastSessionDateInDays(),
  }
}

function createDayIndicatorModel(day: DayDataEntity): DayIndicatorModel {
  return {
    dayOfWeek: day.getDayOfWeek(),
    dayOfMonth: day.getDayOfMonth(),
    isToday: day.isToday(),
    numberOfTrials: day.numberOfTrials,
    numberOfDropIns: day.numberOfDropIns,
    numberOfBirthdays: day.numberOfBirthdays,
    hasTrials: day.hasTrials(),
    hasDropIns: day.hasDropIns(),
    hasBirthdays: day.hasBirthdays(),
    trials: day.trials.map(createSlotParticipantModel),
    dropIns: day.dropIns.map(createSlotParticipantModel),
    birthdaysPresent: day.getBirthdaysPresent().map(createBirthdayMemberModel),
    birthdaysAbsent: day.getBirthdaysAbsent().map(createBirthdayMemberModel),
  }
}

export function createWeeklyTrialsDropInsModel(
  weekData: WeeklyTrialsDropInsEntity | null,
  locale: Language,
  startDate?: string,
  endDate?: string,
): WeeklyTrialsDropInsModel {
  if (!weekData) {
    if (!startDate || !endDate) {
      throw new Error("startDate and endDate are required when weekData is null")
    }
    return createFallbackModel(startDate, endDate, locale)
  }

  const sortedDays = weekData.getSortedDays(locale)

  return {
    weekLabel: weekData.getWeekLabel(locale),
    totalTrials: weekData.getTotalTrials(),
    totalDropIns: weekData.getTotalDropIns(),
    totalBirthdays: weekData.getTotalBirthdays(),
    days: sortedDays.map((day) => createDayIndicatorModel(day)),
    canNavigatePrevious: true,
    canNavigateNext: true,
  }
}

function createFallbackModel(startDate: string, endDate: string, locale: Language): WeeklyTrialsDropInsModel {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  const fallbackDays = Array.from({ length: 7 }, (_, i) => {
    const currentDay = start.add(i, "day")
    return {
      dayOfWeek: currentDay.format("ddd"),
      dayOfMonth: currentDay.date(),
      isToday: currentDay.isSame(dayjs(), "day"),
      numberOfTrials: 0,
      numberOfDropIns: 0,
      numberOfBirthdays: 0,
      hasTrials: false,
      hasDropIns: false,
      hasBirthdays: false,
      trials: [],
      dropIns: [],
      birthdaysPresent: [],
      birthdaysAbsent: [],
    }
  })

  const weekLabel =
    locale === Language.FRENCH
      ? `${start.format("D MMM")} - ${end.format("D MMM")}`
      : `${start.format("MMM D")} - ${end.format("MMM D")}`

  return {
    weekLabel,
    totalTrials: 0,
    totalDropIns: 0,
    totalBirthdays: 0,
    days: fallbackDays,
    canNavigatePrevious: true,
    canNavigateNext: true,
  }
}
